/**
 * The label of an assistant charge in AI activity (TMMAPP-0148 U1 item 9; TMMAPI-0184 U3): the
 * owner of a virtual account pays for every assistant session on it, so the log names who asked and
 * where, as served.
 * - "You asked, in {name}" / "{asker} asked, in {name} (shared)", with ", {virtual account}" where
 *   it's served.
 * - Another member's private room (a `BREAKOUT`), served without its name: "{asker}'s private
 *   room, {virtual account}"; their own `PRIVATE` session on it: "{asker}'s session, {virtual
 *   account}" (TMMAPP-0153 item 12). The person's own room: "My private room, {virtual account}".
 * - An erased asker (`askedBy.name` null) reads "a former member" (catalog copy; TMMAPP-0148 §D),
 *   capitalized where it starts the label.
 * Null for a row that isn't an assistant call.
 */
import type { TFunction } from 'i18next';
import type { AiRequest } from '@/api/aiRequests';

/** Who asked, as served; an erased asker reads "a former member" ("A former member" to start). */
export function aiAskerName(t: TFunction, askedBy: AiRequest['askedBy'], start = false): string {
  const name = askedBy?.name?.trim() ?? '';
  if (name.length > 0) return name;
  return t(start ? 'aiHistory.session.formerMemberStart' : 'aiHistory.session.formerMember');
}

export function aiSessionLabel(t: TFunction, row: AiRequest): string | null {
  const { session, askedBy } = row;
  if (askedBy === null) return null;
  const asker = aiAskerName(t, askedBy, true);
  // The session is gone: who asked is all that's left.
  if (session === null) {
    return askedBy.mine
      ? t('aiHistory.session.mineNoSession')
      : t('aiHistory.session.theirsNoSession', { asker });
  }
  let label: string;
  if (session.kind === 'BREAKOUT' && askedBy.mine) {
    label = t('aiHistory.session.myRoom');
  } else if (session.name === null && !askedBy.mine) {
    // Another member's room or own session: its kind and virtual account only. Only a BREAKOUT is
    // a private room (TMMAPP-0153 item 12); their PRIVATE session on it is their session.
    label =
      session.kind === 'BREAKOUT'
        ? t('aiHistory.session.theirRoom', { asker })
        : t('aiHistory.session.theirSession', { asker });
  } else {
    const name = session.kind === 'SUPPORT' ? t('support.label') : (session.name ?? '');
    const shared = session.kind === 'SHARED';
    label = askedBy.mine
      ? t(shared ? 'aiHistory.session.mineShared' : 'aiHistory.session.mine', { name })
      : t(shared ? 'aiHistory.session.theirsShared' : 'aiHistory.session.theirs', {
          asker,
          name,
        });
  }
  return session.virtualAccountName === null
    ? label
    : t('aiHistory.session.withVirtualAccount', {
        label,
        virtualAccountName: session.virtualAccountName,
      });
}
